import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import type { OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSelectModule } from '@angular/material/select';
import type { ParticipantTrendDialogData } from './participant-trend-dialog-data';
import { ValidatorErrorPipe } from '../../../shared/pipes/validator-error';
import { MindUplotPlaybackComponent } from '../../../shared/components/mind-uplot-playback/mind-uplot-playback.component';
import type { MindUplotOptions } from '../../../shared/components/mind-uplot/model/mind-uplot-options';
import type { MindUplotComponentOptions } from '../../../shared/components/mind-uplot/model/mind-uplot-component-options';
import type { RawClientDataDTO } from '../../../shared/components/mind-uplot/model/raw-client-data.dto';
import { PlaybackStateService } from '../../session/playback-state.service';
import { MindSpinnerComponent } from '../../../shared/components/mind-spinner/mind-spinner.component';
import { IsNilPipe } from '../../../shared/pipes/is-nil.pipe';
import { scaleGradient } from '../../../shared/components/mind-uplot/scales-gradient';
import { isNil } from '@pct/ts-typeguard';
import { filter } from 'rxjs';
import { RecordingService } from '../../session/recording.service';
import { ServerTimeService } from '../../../core/server-time.service';
import { addDeltaToTimeInZone } from '../../../shared/utils/time-converter';

@Component({
  selector: 'app-add-marker-dialog',
  imports: [
    TranslocoDirective,
    MatDialogTitle,
    MatButton,
    MatDialogActions,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelectModule,
    ValidatorErrorPipe,
    MindUplotPlaybackComponent,
    MindSpinnerComponent,
    IsNilPipe,
  ],
  providers: [RecordingService, PlaybackStateService],
  templateUrl: './participant-trend-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class ParticipantTrendDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<ParticipantTrendDialogComponent, boolean>>(MatDialogRef);
  protected readonly data = inject<ParticipantTrendDialogData>(MAT_DIALOG_DATA);
  private readonly recordingService = inject(RecordingService);
  private readonly playbackStateService = inject(PlaybackStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly serverTimeService = inject(ServerTimeService);

  private readonly trendUplot = viewChild<MindUplotPlaybackComponent>('trendUplot');

  protected readonly maxLFControl = new FormControl(1, {
    validators: [Validators.required, Validators.min(1)],
    nonNullable: true,
  });

  protected readonly componentOptions = computed<MindUplotComponentOptions>(() => ({
    dataTypes: ['MF'],
    deviceId: this.data.mindDeviceId,
    evictOldPoints: false,
  }));

  protected readonly options = signal<Partial<MindUplotOptions> | undefined>(undefined);
  protected readonly loading = signal(true);
  protected readonly frames = signal<RawClientDataDTO[] | null>(null);
  protected readonly notEnoughData = signal(false);

  ngOnInit(): void {
    this.maxLFControl.valueChanges
      .pipe(
        filter(() => this.maxLFControl.valid),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(v => {
        this.trendUplot()?.getInstance()?.setScale('y', { min: 0, max: v });
      });

    this.recordingService
      .getTrend(this.data.recordingId, this.data.mindDeviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const data = response.data;
          if (data.length === 0) {
            this.notEnoughData.set(true);
            return;
          }
          const maxValue = Math.max(...data.map(d => d.value));
          this.maxLFControl.setValue(maxValue, { emitEvent: false });
          this.maxLFControl.addValidators(Validators.max(maxValue));

          // TODO: Update consumers to use the participantThresholds array
          const latestParticipantThreshold = response.participantThresholds.at(-1)?.value ?? 0;

          // diagram beállítás
          this.options.set({
            height: 300,
            scales: { x: { time: false }, y: { auto: false, range: [0, maxValue] } },
            hooks: {
              setCursor: [], //default kurzor viselkedés kikapcsolása
              init: [
                u => {
                  u.over.addEventListener('dblclick', () => {
                    this.playbackStateService.resetZoom();
                  });
                },
              ],
            },
            series: [
              {
                label: 'Time',
                value: (_self: unknown, ticks: number) =>
                  isNil(ticks)
                    ? '--:--:--'
                    : addDeltaToTimeInZone(
                        this.data.recordingStartedAt,
                        ticks,
                        this.serverTimeService.serverTimeZone(),
                        'HH:mm:ss.SSS',
                      ),
              },
              {
                width: 1,
                label: 'LF',
                value: (_self: unknown, val: number | null) => val ?? 0,
                stroke: (u, _seriesIdx) =>
                  scaleGradient(
                    u,
                    'y',
                    [
                      [0, '#ff4b4b'],
                      [latestParticipantThreshold, '#366fff'],
                    ],
                    'mf_cache',
                  ),
                points: {
                  fill: (u, _seriesIdx) =>
                    scaleGradient(
                      u,
                      'y',
                      [
                        [0, '#ff4b4b'],
                        [latestParticipantThreshold, '#366fff'],
                      ],
                      'mf_cache',
                    ),
                },
              },
            ],
            axes: [
              {
                stroke: '#eee',
                space: 100,
                values: (_self: unknown, ticks: number[]) =>
                  ticks.map(rawValue =>
                    addDeltaToTimeInZone(
                      this.data.recordingStartedAt,
                      rawValue,
                      this.serverTimeService.serverTimeZone(),
                    ),
                  ),
              },
              { stroke: '#eee' },
            ],
          });

          // frame-ek beállítás
          const lastDelta = data.at(-1)?.delta ?? 1000;
          const firstDelta = data.at(0)?.delta ?? 0;
          const zoomLevel = lastDelta - firstDelta;
          this.playbackStateService.zoomLevel.set(zoomLevel);
          this.playbackStateService.previewHead.set(lastDelta - zoomLevel / 2);

          // frame-ek
          const frames = response.data.map(f => ({
            type: 'MF',
            ...f,
          })) satisfies RawClientDataDTO[];

          this.frames.set(frames);
        },
        complete: () => {
          this.loading.set(false);
        },
      });
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
